import { apiFetch } from '@/lib/api'
import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  ExternalLink,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  RefreshCw,
  Power,
  Trash2,
  RotateCcw,
  Database,
  Copy,
  Check,
} from 'lucide-react'
import { toast } from 'sonner'
import pb from '@/lib/pb'
import { useTenant } from '@/hooks/useTenants'
import { useHealthChecksByTenant } from '@/hooks/useHealthChecks'
import { useProvisionLogsByJob } from '@/hooks/useProvisionLogs'
import { COLLECTIONS, TEARDOWN_MILESTONES, REDEPLOY_MILESTONES } from '@/types'
import {
  formatRelativeTime,
  formatElapsed,
  formatPhone,
  jobStatusVariant,
  tenantStatusVariant,
} from '@/lib/utils'
import { Badge, Button, Modal, Skeleton, SkeletonText } from '@/components/ui'
import { UpdateWizard } from '@/components/tenants/UpdateWizard'
import { HealthTimeline } from '@/components/health/HealthTimeline'
import { ProvisionLogViewer } from '@/components/logs/ProvisionLogViewer'
import { TenantLogViewer } from '@/components/tenants/TenantLogViewer'
import type { TJob, TTenantProvisionEnv, TTenantSecret } from '@/types'

// ── Resource bar color ─────────────────────────────────────────────────────

function resourceBarColor(value: number): string {
  if (value >= 90) return 'var(--status-unhealthy)'
  if (value >= 70) return 'var(--status-warning)'
  return 'var(--status-healthy)'
}

// ── Tenant Detail Page ─────────────────────────────────────────────────────

export default function TenantDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState<'overview' | 'logs'>('overview')
  const [updateWizardOpen, setUpdateWizardOpen] = useState(false)
  const [pbPassCopied, setPbPassCopied] = useState(false)
  const [n8nPassCopied, setN8nPassCopied] = useState(false)
  const [n8nKeyCopied, setN8nKeyCopied] = useState(false)
  const [confirmAction, setConfirmAction] = useState<'teardown' | 'delete' | 'redeploy' | null>(null)
  const [confirmSlug, setConfirmSlug] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [activeAction, setActiveAction] = useState<'teardown' | 'redeploy' | null>(null)
  const [actionJobId, setActionJobId] = useState<string | null>(null)
  const [actionStartedAt, setActionStartedAt] = useState<string | null>(null)

  const { data: tenant, isLoading: tenantLoading } = useTenant(id ?? '')
  const { data: healthChecks = [] } = useHealthChecksByTenant(id ?? '')
  const latestHealth = healthChecks[0]

  // Logs for the active teardown/redeploy — job ID → provision → logs
  const { data: actionLogs = [], provisionId: actionProvisionId } = useProvisionLogsByJob(actionJobId)

  // Detect completion of the active action
  const actionMilestones = activeAction === 'teardown' ? TEARDOWN_MILESTONES : REDEPLOY_MILESTONES
  const completeMilestoneId = activeAction === 'teardown' ? 'teardown_complete' : 'redeploy_complete'
  const isActionComplete = useMemo(
    () => actionLogs.some(l => l.milestone === completeMilestoneId && l.milestone_ended),
    [actionLogs, completeMilestoneId]
  )
  const isActionFailed = useMemo(
    () => activeAction ? actionLogs.some(l => l.level === 'error' && l.message?.toLowerCase().includes('failed')) : false,
    [actionLogs, activeAction]
  )
  const actionCompletedAt = useMemo(() => {
    if (!isActionComplete && !isActionFailed) return null
    const lastLog = actionLogs[actionLogs.length - 1]
    return lastLog?.timestamp ?? new Date().toISOString()
  }, [isActionComplete, isActionFailed, actionLogs])

  const { data: recentJobsResult } = useQuery({
    queryKey: ['jobs', 'tenant', id],
    queryFn: () => pb.collection(COLLECTIONS.JOBS).getList<TJob>(1, 5, {
      filter: `tenant = "${id}"`,
    }),
    enabled: !!id,
  })

  const recentJobs = recentJobsResult?.items ?? []

  // ── Environment Variables & Secrets ───────────────────────────────────
  const [envExpanded, setEnvExpanded] = useState(false)
  const [secretsExpanded, setSecretsExpanded] = useState(false)
  const [revealedEnvKeys, setRevealedEnvKeys] = useState<Set<string>>(new Set())
  const [revealedSecretIds, setRevealedSecretIds] = useState<Set<string>>(new Set())

  const { data: envSnapshot } = useQuery({
    queryKey: ['tenant-env', tenant?.last_known_good_provision],
    queryFn: () =>
      pb.collection(COLLECTIONS.TENANT_PROVISION_ENV).getFirstListItem<TTenantProvisionEnv>(
        `provision = "${tenant!.last_known_good_provision}"`
      ),
    enabled: !!tenant?.last_known_good_provision,
  })

  const { data: tenantSecretsResult } = useQuery({
    queryKey: ['tenant-secrets', id],
    queryFn: () =>
      pb.collection(COLLECTIONS.TENANT_SECRETS).getList<TTenantSecret>(1, 50, {
        filter: `tenant = "${id}"`,
      }),
    enabled: !!id,
  })

  const tenantSecrets = tenantSecretsResult?.items ?? []

  const isSensitiveKey = (key: string) =>
    /PASSWORD|KEY|TOKEN|SECRET/i.test(key)

  const toggleEnvReveal = (key: string) => {
    setRevealedEnvKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleSecretReveal = (id: string) => {
    setRevealedSecretIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleAction = async (action: 'teardown' | 'delete' | 'redeploy') => {
    if (!tenant) return
    setActionLoading(true)
    try {
      const method = action === 'delete' ? 'DELETE' : 'POST'
      const url = action === 'delete' ? `/api/tenants/${tenant.id}` : `/api/tenants/${tenant.id}/${action}`
      const res = await apiFetch(url, { method, body: action === 'delete' ? JSON.stringify({ confirm: true }) : undefined })
      if (!res.ok) { const err = await res.json().catch(() => ({ message: `${action} failed` })); throw new Error(err.message ?? `${action} failed: ${res.status}`) }
      const data = await res.json().catch(() => ({}))
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)} started`)
      setConfirmAction(null); setConfirmSlug('')
      // For teardown/redeploy, switch to progress modal
      if (action === 'teardown' || action === 'redeploy') {
        setActiveAction(action)
        setActionJobId(data.job_id ?? null)
        setActionStartedAt(new Date().toISOString())
      }
    } catch (err) { toast.error(err instanceof Error ? err.message : `${action} failed`) } finally { setActionLoading(false) }
  }

  if (tenantLoading) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <Skeleton width={80} height={32} borderRadius="var(--radius-md)" />
            <Skeleton width={200} height={20} />
          </div>
          <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '20px' }}>
            <SkeletonText lines={6} />
          </div>
        </div>
      </div>
    )
  }

  if (!tenant) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 12px' }}>Tenant not found</h2>
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '0 0 20px' }}>This tenant may have been archived or deleted.</p>
        <Button variant="primary" onClick={() => navigate('/tenants')}>Back to Tenants</Button>
      </div>
    )
  }

  // tenant_url is already full URL like "https://slug.deconstraint.ai" or just "slug.deconstraint.ai"
  const externalUrl = tenant.tenant_url?.startsWith('http') ? tenant.tenant_url : `https://${tenant.tenant_url || `${tenant.slug}.deconstraint.ai`}`
  const isProvisioning = tenant.status === 'provisioning'

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Button variant="ghost" size="sm" icon={<ArrowLeft size={16} />} onClick={() => navigate('/tenants')} aria-label="Back to Tenants" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
              <span style={{ color: 'var(--text-tertiary)', cursor: 'pointer' }} onClick={() => navigate('/tenants')}>Tenants</span>
              <ChevronRight size={14} style={{ color: 'var(--text-tertiary)' }} />
              <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{tenant.name}</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {tenant.status === 'active' && (
              <>
                <Button variant="primary" size="sm" icon={<ExternalLink size={14} />} onClick={() => window.open(externalUrl, '_blank')}>Open Tenant</Button>
                <Button variant="success" size="sm" icon={<RefreshCw size={14} />} onClick={() => setUpdateWizardOpen(true)}>Update</Button>
                <Button variant="danger" size="sm" icon={<Power size={14} />} onClick={() => setConfirmAction('teardown')}>Tear Down</Button>
              </>
            )}
            {tenant.status === 'dormant' && (
              <>
                <Button variant="primary" size="sm" icon={<RotateCcw size={14} />} onClick={() => setConfirmAction('redeploy')}>Redeploy</Button>
                <Button variant="danger" size="sm" icon={<Trash2 size={14} />} onClick={() => setConfirmAction('delete')}>Delete</Button>
              </>
            )}
            {(tenant.status === 'provisioning' || tenant.status === 'provision_failed') && (
              <Button variant="danger" size="sm" icon={<Power size={14} />} onClick={() => setConfirmAction('teardown')}>Tear Down</Button>
            )}
          </div>
        </div>

        {/* Tab Bar */}
        <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid var(--border-subtle)', marginBottom: '24px' }}>
          {(['overview', 'logs'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '8px 16px',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid var(--brand)' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: activeTab === tab ? 600 : 400,
                color: activeTab === tab ? 'var(--brand)' : 'var(--text-secondary)',
                textTransform: 'capitalize',
                marginBottom: '-1px',
              }}
            >
              {tab === 'overview' ? 'Overview' : 'Logs'}
            </button>
          ))}
        </div>

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 16px' }}>
              Container Logs
            </h3>
            <TenantLogViewer tenantSlug={tenant.slug} />
          </div>
        )}

        {activeTab === 'overview' && <>

        {/* Business Information */}
        <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 20px' }}>Business Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* Left: Business */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <DetailField label="Business Name" value={tenant.legal_name || tenant.name} />
              <DetailField label="Website">
                {tenant.website ? (
                  <a href={tenant.website.startsWith('http') ? tenant.website : `https://${tenant.website}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'var(--brand)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    {tenant.website}<ExternalLink size={11} style={{ opacity: 0.6 }} />
                  </a>
                ) : (
                  <span style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>---</span>
                )}
              </DetailField>
              <DetailField label="Industry" value={tenant.industry || '---'} />
            </div>
            {/* Right: Contact */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <DetailField label="Contact Name" value={tenant.primary_contact_name || '---'} />
              <DetailField label="Contact Email" value={tenant.primary_contact_email || '---'} />
              <DetailField label="Contact Phone" value={formatPhone(tenant.primary_contact_phone)} />
            </div>
          </div>
        </div>

        {/* Deployment Information */}
        <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 20px' }}>Deployment Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* Left: Infrastructure */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <DetailField label="Tenant URL">
                <a href={externalUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', fontFamily: 'var(--font-mono)', color: 'var(--brand)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  {tenant.tenant_url || `${tenant.slug}.deconstraint.ai`}<ExternalLink size={11} style={{ opacity: 0.6 }} />
                </a>
              </DetailField>
              <DetailField label="Status">
                <Badge variant={tenantStatusVariant(tenant.status)} dot pulse={isProvisioning}>
                  {isProvisioning ? <ProvisioningTimer created={tenant.created} /> : tenant.status.replace('_', ' ')}
                </Badge>
              </DetailField>
              <DetailField label="Slug" value={tenant.slug} mono />
              <DetailField label="Droplet IP" value={tenant.droplet_ip || '---'} mono />
              <DetailField label="TLS Status" value={tenant.tls_status || '---'} />
              <DetailField label="PocketBase Admin">
                {tenant.tenant_url ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <a href={`${tenant.tenant_url}/_/`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'var(--brand)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Database size={12} /> Open Dashboard <ExternalLink size={10} style={{ opacity: 0.6 }} />
                    </a>
                    {envSnapshot?.env_snapshot && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                        <span style={{ fontFamily: 'var(--font-mono)' }}>{envSnapshot.env_snapshot.PB_ADMIN_EMAIL || 'admin@deconstraint.com'}</span>
                        <button
                          onClick={() => {
                            const pw = envSnapshot.env_snapshot.PB_ADMIN_PASSWORD
                            if (pw) {
                              navigator.clipboard.writeText(pw)
                              setPbPassCopied(true)
                              setTimeout(() => setPbPassCopied(false), 2000)
                            }
                          }}
                          title="Copy admin password"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 6px', fontSize: '10px', color: pbPassCopied ? 'var(--status-healthy)' : 'var(--text-tertiary)', backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}
                        >
                          {pbPassCopied ? <><Check size={10} /> Copied</> : <><Copy size={10} /> Password</>}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <span style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>---</span>
                )}
              </DetailField>
              <DetailField label="n8n Admin">
                {tenant.n8n_status === 'provisioned' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <a href={`https://n8n.${tenant.slug}.deconstraint.ai/`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'var(--brand)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Database size={12} /> Open n8n <ExternalLink size={10} style={{ opacity: 0.6 }} />
                    </a>
                    {envSnapshot?.env_snapshot && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-tertiary)', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'var(--font-mono)' }}>{envSnapshot.env_snapshot.PB_ADMIN_EMAIL || 'admin@deconstraint.com'}</span>
                        <button
                          onClick={() => {
                            const pw = envSnapshot.env_snapshot.PB_ADMIN_PASSWORD
                            if (pw) {
                              navigator.clipboard.writeText(pw)
                              setN8nPassCopied(true)
                              setTimeout(() => setN8nPassCopied(false), 2000)
                            }
                          }}
                          title="Copy n8n owner password (same as PB admin)"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 6px', fontSize: '10px', color: n8nPassCopied ? 'var(--status-healthy)' : 'var(--text-tertiary)', backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}
                        >
                          {n8nPassCopied ? <><Check size={10} /> Copied</> : <><Copy size={10} /> Password</>}
                        </button>
                        {tenantSecretsResult?.items.find(s => s.key_name === 'N8N_API_KEY') && (
                          <button
                            onClick={() => {
                              const k = tenantSecretsResult.items.find(s => s.key_name === 'N8N_API_KEY')?.key_value
                              if (k) {
                                navigator.clipboard.writeText(k)
                                setN8nKeyCopied(true)
                                setTimeout(() => setN8nKeyCopied(false), 2000)
                              }
                            }}
                            title="Copy platform-ares API key (used by ARES to drive n8n)"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 6px', fontSize: '10px', color: n8nKeyCopied ? 'var(--status-healthy)' : 'var(--text-tertiary)', backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}
                          >
                            {n8nKeyCopied ? <><Check size={10} /> Copied</> : <><Copy size={10} /> API key</>}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ) : tenant.n8n_status === 'unprovisioned' ? (
                  <span style={{ fontSize: '12px', color: 'var(--status-warning)' }}>not bootstrapped — POST /api/tenants/{tenant.id}/n8n/bootstrap</span>
                ) : (
                  <span style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>---</span>
                )}
              </DetailField>
            </div>
            {/* Right: Versions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <DetailField label="Web App Version" value={tenant.web_app_version || '---'} mono />
              <DetailField label="OpenClaw Version" value={tenant.openclaw_version || '---'} mono />
              <DetailField label="PocketBase Tag" value={tenant.pocketbase_tag || '---'} mono />
              <DetailField label="Caddy Tag" value={tenant.caddy_tag || '---'} mono />
              <DetailField label="n8n Version" value={tenant.n8n_version || (tenant.n8n_status === 'unprovisioned' ? 'unprovisioned' : '---')} mono />
            </div>
          </div>
        </div>

        {/* Environment Variables */}
        {envSnapshot?.env_snapshot && Object.keys(envSnapshot.env_snapshot).length > 0 && (
          <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', marginBottom: '20px', overflow: 'hidden' }}>
            <button
              onClick={() => setEnvExpanded(prev => !prev)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}
            >
              <h3 style={{ fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0, color: 'var(--text-tertiary)' }}>
                Environment Variables
                <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>
                  ({Object.keys(envSnapshot.env_snapshot).length} vars)
                </span>
              </h3>
              {envExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {envExpanded && (
              <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '16px 24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) 2fr auto', gap: '0', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--border-subtle)' }}>
                  {/* Header */}
                  <div style={{ padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-subtle)' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Key</span>
                  </div>
                  <div style={{ padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-subtle)' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Value</span>
                  </div>
                  <div style={{ padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-subtle)' }} />
                  {/* Rows */}
                  {Object.entries(envSnapshot.env_snapshot).map(([key, value]) => {
                    const sensitive = isSensitiveKey(key)
                    const revealed = revealedEnvKeys.has(key)
                    return (
                      <div key={key} style={{ display: 'contents' }}>
                        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', fontWeight: 500, wordBreak: 'break-all' }}>{key}</span>
                        </div>
                        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', minWidth: 0 }}>
                          <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', wordBreak: 'break-all', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {sensitive && !revealed ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022' : value}
                          </span>
                        </div>
                        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {sensitive && (
                            <button
                              onClick={() => toggleEnvReveal(key)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center' }}
                              title={revealed ? 'Hide value' : 'Reveal value'}
                            >
                              {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tenant Secrets */}
        {tenantSecrets.length > 0 && (
          <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', marginBottom: '20px', overflow: 'hidden' }}>
            <button
              onClick={() => setSecretsExpanded(prev => !prev)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}
            >
              <h3 style={{ fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0, color: 'var(--text-tertiary)' }}>
                Tenant Secrets
                <span style={{ marginLeft: '8px', fontSize: '11px', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>
                  ({tenantSecrets.length} secrets)
                </span>
              </h3>
              {secretsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {secretsExpanded && (
              <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '16px 24px' }}>
                <div style={{ borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--border-subtle)' }}>
                  {/* Header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr auto', gap: '0' }}>
                    <div style={{ padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Key</span>
                    </div>
                    <div style={{ padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Value</span>
                    </div>
                    <div style={{ padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Source</span>
                    </div>
                    <div style={{ padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-subtle)' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Generated</span>
                    </div>
                    <div style={{ padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-subtle)' }} />
                  </div>
                  {/* Rows */}
                  {tenantSecrets.map(secret => {
                    const revealed = revealedSecretIds.has(secret.id)
                    return (
                      <div key={secret.id} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr auto', gap: '0' }}>
                        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', fontWeight: 500, wordBreak: 'break-all' }}>{secret.key_name}</span>
                        </div>
                        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', minWidth: 0 }}>
                          <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', wordBreak: 'break-all', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {revealed ? secret.key_value : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}
                          </span>
                        </div>
                        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center' }}>
                          <Badge variant="default">{secret.source}</Badge>
                        </div>
                        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{secret.generated_at ? formatRelativeTime(secret.generated_at) : '---'}</span>
                        </div>
                        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <button
                            onClick={() => toggleSecretReveal(secret.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center' }}
                            title={revealed ? 'Hide value' : 'Reveal value'}
                          >
                            {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Health Section */}
        <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 20px' }}>Health</h3>
          {tenant.status !== 'active' ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
              Health checks paused &mdash; tenant is {tenant.status.replace('_', ' ')}
            </div>
          ) : latestHealth ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                {(() => {
                  const isStale = Date.now() - new Date(latestHealth.checked_at).getTime() > 60_000
                  if (isStale) {
                    return <Badge variant="warning" dot>Stale</Badge>
                  }
                  return <Badge variant={latestHealth.is_healthy ? 'success' : 'error'} dot pulse={latestHealth.is_healthy}>{latestHealth.overall_status}</Badge>
                })()}
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Last check: {formatRelativeTime(latestHealth.checked_at)}</span>
              </div>
              {latestHealth.resource_usage && (typeof latestHealth.resource_usage.cpu_percent === 'number' || typeof latestHealth.resource_usage.memory_percent === 'number' || typeof latestHealth.resource_usage.disk_percent === 'number') && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <ResourceBar label="CPU" value={latestHealth.resource_usage.cpu_percent ?? 0} />
                  <ResourceBar label="Memory" value={latestHealth.resource_usage.memory_percent ?? 0} />
                  <ResourceBar label="Disk" value={latestHealth.resource_usage.disk_percent ?? 0} />
                </div>
              )}
              <div>
                <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '10px', display: 'block' }}>Services</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  {(['caddy', 'pocketbase', 'frontend', 'action_runner', 'openclaw', 'n8n'] as const).map(service => {
                    const check = latestHealth.services?.[service]; if (!check) return null; const isUp = check.status === 'up'
                    return (<span key={service} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}><span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: isUp ? 'var(--status-healthy)' : 'var(--status-unhealthy)' }} />{service.replace('_', ' ')}</span>)
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>No health data available. Run a health check to populate.</div>
          )}
        </div>

        {/* Health Timeline — only shown when tenant is active */}
        {tenant.status === 'active' && (
          <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 20px' }}>Health Timeline</h3>
            <HealthTimeline tenantId={tenant.id} />
          </div>
        )}

        {/* Recent Jobs */}
        <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border-subtle)' }}>
            <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>Recent Jobs</h3>
          </div>
          {recentJobs.length > 0 ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '12px', padding: '10px 24px', borderBottom: '1px solid var(--border-subtle)' }}>
                {['Type', 'Status', 'Duration', 'Created'].map(col => (<span key={col} style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{col}</span>))}
              </div>
              {recentJobs.map(job => (
                <div key={job.id} className="table-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '12px', padding: '12px 24px', borderBottom: '1px solid var(--border-subtle)', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.title || job.job_type}</span>
                  <span><Badge variant={jobStatusVariant(job.status)}>{job.status}</Badge></span>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}><JobDuration job={job} /></span>
                  <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{formatRelativeTime(job.created)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>No jobs for this tenant</div>
          )}
        </div>
        </> /* end activeTab === 'overview' */}
      </div>

      {/* Update Wizard */}
      {tenant && <UpdateWizard isOpen={updateWizardOpen} onClose={() => setUpdateWizardOpen(false)} tenant={tenant} />}

      {/* Confirm Action Modal */}
      <Modal
        isOpen={!!confirmAction}
        onClose={() => { setConfirmAction(null); setConfirmSlug('') }}
        title={confirmAction === 'teardown' ? 'Tear Down Tenant' : confirmAction === 'delete' ? 'Delete Tenant' : confirmAction === 'redeploy' ? 'Redeploy Tenant' : 'Confirm Action'}
        subtitle={
          confirmAction === 'teardown'
            ? `Are you sure you want to tear down ${tenant.name}?`
            : confirmAction === 'delete'
              ? 'This will permanently archive this tenant. Type the slug to confirm.'
              : confirmAction === 'redeploy'
                ? 'This will redeploy the tenant from its last known good state.'
                : undefined
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => { setConfirmAction(null); setConfirmSlug('') }}>Cancel</Button>
            <Button
              variant={confirmAction === 'redeploy' ? 'primary' : 'danger'}
              disabled={actionLoading || ((confirmAction === 'delete' || confirmAction === 'teardown') && confirmSlug !== tenant?.slug)}
              onClick={() => confirmAction && handleAction(confirmAction)}
            >
              {actionLoading ? 'Processing...' : confirmAction === 'teardown' ? 'Confirm Tear Down' : `Confirm ${confirmAction ?? ''}`}
            </Button>
          </>
        }
      >
        {confirmAction === 'teardown' && tenant && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
              All containers will be stopped. The Droplet, volumes, and DNS records will remain intact. You can redeploy later.
            </p>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>
              Type <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{tenant.slug}</strong> to confirm.
            </p>
            <input
              className="input-field"
              placeholder={tenant.slug}
              value={confirmSlug}
              onChange={e => setConfirmSlug(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', outline: 'none' }}
            />
          </div>
        )}
        {confirmAction === 'delete' && tenant && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>Type <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{tenant.slug}</strong> to confirm deletion.</p>
            <input
              className="input-field"
              placeholder={tenant.slug}
              value={confirmSlug}
              onChange={e => setConfirmSlug(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', outline: 'none' }}
            />
          </div>
        )}
        {confirmAction === 'redeploy' && <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>The tenant will be redeployed using the last known good provision configuration.</p>}
      </Modal>

      {/* Teardown/Redeploy Progress Modal */}
      <Modal
        isOpen={!!activeAction}
        onClose={() => { if (isActionComplete || isActionFailed) { setActiveAction(null); setActionJobId(null); setActionStartedAt(null) } }}
        title={activeAction === 'teardown' ? 'Tearing Down Tenant' : activeAction === 'redeploy' ? 'Redeploying Tenant' : 'Action in Progress'}
        subtitle={tenant?.name}
        maxWidth="640px"
        footer={
          <Button
            variant="primary"
            onClick={() => { setActiveAction(null); setActionJobId(null); setActionStartedAt(null) }}
            disabled={!isActionComplete && !isActionFailed}
          >
            {isActionComplete ? 'Done' : isActionFailed ? 'Close' : 'In Progress...'}
          </Button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <ActionMilestoneProgress
            logs={actionLogs}
            milestones={actionMilestones}
            startedAt={actionStartedAt}
            completedAt={actionCompletedAt}
          />
          <ProvisionLogViewer provisionId={actionProvisionId} maxHeight="240px" />
        </div>
      </Modal>
    </div>
  )
}

// ── Action Milestone Progress (reusable for teardown/redeploy) ────────────

import { CheckCircle, Loader, Circle, XCircle } from 'lucide-react'
import { formatDuration } from '@/lib/utils'
import type { TProvisionLog, MilestoneDefinition } from '@/types'

interface ActionMilestoneProgressProps {
  logs: TProvisionLog[]
  /** `readonly` so the `as const satisfies` catalog arrays are accepted. */
  milestones: readonly MilestoneDefinition[]
  startedAt: string | null | undefined
  completedAt?: string | null
}

interface ActionMilestoneState {
  id: string
  label: string
  order: number
  status: 'complete' | 'in_progress' | 'pending' | 'error'
  startTime?: string
  endTime?: string
  deltaMs?: number
}

function ActionMilestoneProgress({ logs, milestones, startedAt, completedAt }: ActionMilestoneProgressProps) {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (completedAt) return
    if (!startedAt) return
    const interval = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(interval)
  }, [startedAt, completedAt])

  const milestoneStates = useMemo<ActionMilestoneState[]>(() => {
    const milestoneLogs = logs.filter(l => l.level === 'milestone' && l.milestone)
    const hasError = logs.some(l => l.level === 'error' && l.message?.toLowerCase().includes('failed'))
    const isRunning = !completedAt && !!startedAt

    const result = milestones.map(def => {
      const matchingLogs = milestoneLogs.filter(l => l.milestone === def.id)
      const started = matchingLogs.find(l => l.milestone_started)
      const ended = matchingLogs.find(l => l.milestone_ended)
      let status: ActionMilestoneState['status'] = 'pending'
      if (ended?.milestone_ended) status = 'complete'
      else if (started?.milestone_started) status = hasError ? 'error' : 'in_progress'
      return {
        id: def.id, label: def.label, order: def.order, status,
        startTime: started?.milestone_started ?? undefined,
        endTime: ended?.milestone_ended ?? undefined,
        deltaMs: ended?.milestone_delta_ms ?? undefined,
      }
    })

    if (isRunning && !hasError) {
      const lastCompleteIdx = result.reduce((acc, m, i) => m.status === 'complete' ? i : acc, -1)
      const nextPending = result.find((m, i) => i > lastCompleteIdx && m.status === 'pending')
      if (nextPending) nextPending.status = 'in_progress'
    }

    if (hasError) {
      const inProgress = result.find(m => m.status === 'in_progress')
      if (inProgress) {
        inProgress.status = 'error'
      } else {
        const lastCompleteIdx = result.reduce((acc, m, i) => m.status === 'complete' ? i : acc, -1)
        const nextPending = result.find((m, i) => i > lastCompleteIdx && m.status === 'pending')
        if (nextPending) nextPending.status = 'error'
      }
    }

    return result
  }, [logs, milestones, startedAt, completedAt])

  const completedCount = milestoneStates.filter(m => m.status === 'complete').length
  const totalCount = milestoneStates.length
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0
  const isRunning = !completedAt && !!startedAt

  const elapsedMs = useMemo(() => {
    void tick
    if (!startedAt) return 0
    const start = new Date(startedAt).getTime()
    if (completedAt) return new Date(completedAt).getTime() - start
    return Date.now() - start
  }, [startedAt, completedAt, tick])

  function fmtTime(ts: string | undefined): string {
    if (!ts) return '--:--'
    try { return new Date(ts).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }) }
    catch { return '--:--' }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{completedCount} of {totalCount} milestones</span>
          <span style={{ fontSize: '12px', fontFamily: 'var(--font-mono)', color: isRunning ? 'var(--status-provisioning)' : 'var(--text-secondary)' }}>
            {isRunning ? 'Elapsed: ' : completedAt ? 'Total: ' : ''}{formatDuration(elapsedMs)}
          </span>
        </div>
        <div style={{ height: '6px', backgroundColor: 'var(--bg-tertiary)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
          <div style={{ width: `${progressPercent}%`, height: '100%', backgroundColor: completedCount === totalCount ? 'var(--status-healthy)' : 'var(--status-provisioning)', borderRadius: 'var(--radius-full)', transition: 'width 500ms ease' }} />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {milestoneStates.map(m => (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 8px', borderRadius: 'var(--radius-sm)', backgroundColor: m.status === 'in_progress' ? 'var(--status-provisioning-dim)' : m.status === 'error' ? 'var(--status-unhealthy-dim)' : 'transparent' }}>
            <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              {m.status === 'complete' ? <CheckCircle size={16} style={{ color: 'var(--status-healthy)' }} /> : m.status === 'in_progress' ? <Loader size={16} style={{ color: 'var(--status-provisioning)', animation: 'spin 1s linear infinite' }} /> : m.status === 'error' ? <XCircle size={16} style={{ color: 'var(--status-unhealthy)' }} /> : <Circle size={16} style={{ color: 'var(--text-tertiary)', opacity: 0.5 }} />}
            </span>
            <span style={{ flex: 1, fontSize: '13px', fontWeight: m.status === 'in_progress' || m.status === 'error' ? 500 : 400, color: m.status === 'pending' ? 'var(--text-tertiary)' : m.status === 'error' ? 'var(--status-unhealthy)' : 'var(--text-primary)' }}>{m.label}</span>
            {(m.startTime || m.endTime) && <span style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)', flexShrink: 0 }}>{fmtTime(m.startTime)}{m.endTime ? ` - ${fmtTime(m.endTime)}` : ''}</span>}
            {m.deltaMs != null && m.deltaMs > 0 && <span style={{ fontSize: '11px', fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-secondary)', flexShrink: 0, minWidth: '44px', textAlign: 'right' }}>{formatDuration(m.deltaMs)}</span>}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Detail Field ───────────────────────────────────────────────────────────

interface DetailFieldProps { label: string; value?: string; mono?: boolean; children?: React.ReactNode }

function DetailField({ label, value, mono, children }: DetailFieldProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
      {children ?? (<span style={{ fontSize: '13px', color: value === '---' ? 'var(--text-tertiary)' : 'var(--text-primary)', fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)', wordBreak: 'break-all' }}>{value || '---'}</span>)}
    </div>
  )
}

// ── Resource Bar ───────────────────────────────────────────────────────────

function ResourceBar({ label, value }: { label: string; value: number }) {
  const color = resourceBarColor(value)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-secondary)' }}>{label}</span>
        <span style={{ fontSize: '12px', fontWeight: 600, fontFamily: 'var(--font-mono)', color }}>{Math.round(value)}%</span>
      </div>
      <div style={{ width: '100%', height: '6px', backgroundColor: 'var(--bg-tertiary)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, Math.max(0, value))}%`, height: '100%', backgroundColor: color, borderRadius: 'var(--radius-full)', transition: 'width 300ms ease' }} />
      </div>
    </div>
  )
}

// ── Provisioning Timer ─────────────────────────────────────────────────────

function ProvisioningTimer({ created }: { created: string }) {
  const [, setTick] = useState(0)
  useEffect(() => { const interval = setInterval(() => setTick(t => t + 1), 1000); return () => clearInterval(interval) }, [])
  return <span>Provisioning... {formatElapsed(created)}</span>
}

// ── Job Duration ───────────────────────────────────────────────────────────

function JobDuration({ job }: { job: TJob }) {
  const [, setTick] = useState(0)
  useEffect(() => { if (job.status !== 'running') return; const interval = setInterval(() => setTick(t => t + 1), 1000); return () => clearInterval(interval) }, [job.status])
  if (job.status === 'running' && job.started_at) return <span>{formatElapsed(job.started_at)}</span>
  if (job.started_at && job.completed_at) { const diffSec = Math.round((new Date(job.completed_at).getTime() - new Date(job.started_at).getTime()) / 1000); if (diffSec < 60) return <span>{diffSec}s</span>; return <span>{Math.floor(diffSec / 60)}m {(diffSec % 60).toString().padStart(2, '0')}s</span> }
  return <span>---</span>
}
